import { VenueFullProfileImageSize } from '@/models/VenueFullProfileImageSize';
import { VENUE_PREFIX, URL_SLASH } from '@/constants/routeConstants';
import { queryStringConstants } from '@/constants/queryConstants';
import UrlBuilder, { type LocationObject } from '@/utils/urlBuilder';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import type { Locale } from '@/i18n';
import { SearchParams } from '@/models/SearchParams';
import { addExistingParamsToUrl, addVenueProfileFactParamsToUrl } from '@/utils/urlHelper';
import type { VenueProfileFactParams } from '@/components/card/VenueCard';
import { isProdEnv } from '@/utils/envHelper';

const EMPTY_STRING = '';

function slugify(str = ''): string {
  return str
    .trim()
    .replace(/[^\u00C0-\u017Fa-zA-Z0-9]+/g, '-')
    .toLowerCase();
}

export function getOfferingImage(offeringImage: VenueFullProfileImageSize | string | undefined): string | undefined {
  // ofrgImage is an object with image variants
  let ofrgImage: string | undefined;
  if (typeof offeringImage === 'object') {
    ofrgImage = offeringImage.original;
  } else {
    ofrgImage = offeringImage;
  }

  // prepend https if not exist since venues endpoint from venue service doesn't return it
  if (typeof ofrgImage === 'string' && !/^https?:\/\//i.test(ofrgImage)) {
    ofrgImage = `https://${ofrgImage}`;
  }
  return ofrgImage;
}

/**
 * This function adds a venue URL to a URL builder
 */
export function addVenueUrlBuilder(
  urlBuilder: UrlBuilder,
  name: string,
  type: string,
  city: string,
  id: string,
  isPreview: boolean = false
): UrlBuilder {
  addPreviewQueryObject(urlBuilder, isPreview);
  urlBuilder.addPath(slugify(city));
  urlBuilder.addPath(slugify(type));
  urlBuilder.addPath(slugify(name));
  urlBuilder.addPath(`${VENUE_PREFIX}${id}`);
  return urlBuilder;
}

/*
 * This function adds privew query object to the URL
 */
export function addPreviewQueryObject(urlBuilder, isPreview) {
  if (isPreview) {
    urlBuilder.addQueryParam(queryStringConstants.IS_PREVIEW, 1);
  }
  return urlBuilder;
}

/*
 * This function creates a venue URL based on the venue ID, name, type, city, and locale.
 */
export function createVenueURL(
  id: string,
  name: string,
  type: string,
  city: string,
  locale: Locale,
  cventGuestDomain: string,
  basePath: string,
  searchParams?: SearchParams,
  venueProfileFactParams?: VenueProfileFactParams,
  isPreview?: boolean
): LocationObject {
  const urlBuilder = new UrlBuilder();
  urlBuilder.addPath(cventGuestDomain);
  urlBuilder.addPath(basePath.startsWith(URL_SLASH) ? basePath.slice(1) : basePath);
  urlBuilder.addPath(locale);
  addVenueUrlBuilder(urlBuilder, name, type, city, id, isPreview);
  if (searchParams) {
    addExistingParamsToUrl(urlBuilder, searchParams);
  }
  if (venueProfileFactParams) {
    addVenueProfileFactParamsToUrl(urlBuilder, venueProfileFactParams);
  }
  return urlBuilder.getLocationObject();
}

/*
 * This function creates a URL for the BRAND PAGE. It takes the listing ID, brand name, and listing code as parameters
 */
export function createBrandPageURLFromListing(
  id: string,
  brandName: string | undefined,
  cventGuestDomain: string,
  listingCode?: string,
  searchParams?: SearchParams,
  venueProfileFactParams?: VenueProfileFactParams
): LocationObject {
  const sShortStub = id.replace(/-/g, '');
  const urlBuilder = new UrlBuilder();
  urlBuilder.addPath(cventGuestDomain);
  urlBuilder.addPath(queryStringConstants.RFP);
  urlBuilder.addPath(`${slugify(brandName)}-${sShortStub}.aspx`);
  urlBuilder.addQueryParam(queryStringConstants.REFERRAL, listingCode, false, true);
  if (searchParams) {
    addExistingParamsToUrl(urlBuilder, searchParams);
  }
  if (venueProfileFactParams) {
    addVenueProfileFactParamsToUrl(urlBuilder, venueProfileFactParams);
  }
  return urlBuilder.getLocationObject();
}

/**
 * Create a URL to a venue page from a listing code
 */
export function createAdPageURLFromListing(
  id: string,
  name: string,
  type: string,
  city: string,
  locale: Locale,
  cventGuestDomain: string,
  basePath: string,
  listingCode?: string,
  searchParams?: SearchParams,
  venueProfileFactParams?: VenueProfileFactParams
): LocationObject {
  const urlBuilder = new UrlBuilder();
  urlBuilder.addPath(cventGuestDomain);
  urlBuilder.addPath(basePath.startsWith('/') ? basePath.substring(1) : basePath);
  urlBuilder.addPath(locale);
  addVenueUrlBuilder(urlBuilder, name, type, city, id);
  urlBuilder.addQueryParam(queryStringConstants.AD_CODE, listingCode, false, true);
  if (searchParams) {
    addExistingParamsToUrl(urlBuilder, searchParams);
  }
  if (venueProfileFactParams) {
    addVenueProfileFactParamsToUrl(urlBuilder, venueProfileFactParams);
  }
  return urlBuilder.getLocationObject();
}

/**
 * This code creates a URL with a query string that contains the RFP ID and environment.
 */
export function createAdPageUrlFromPageUrl(
  pageUrl: string,
  cventGuestDomain: string,
  searchParams?: SearchParams,
  venueProfileFactParams?: VenueProfileFactParams,
  environment: string = EMPTY_STRING
): LocationObject {
  const urlBuilder = new UrlBuilder();
  urlBuilder.addPath(cventGuestDomain);
  urlBuilder.addPath(pageUrl);
  if (searchParams) {
    addExistingParamsToUrl(urlBuilder, searchParams);
  }
  if (venueProfileFactParams) {
    addVenueProfileFactParamsToUrl(urlBuilder, venueProfileFactParams);
  }
  if (!isProdEnv(environment)) {
    urlBuilder.addQueryParam(queryStringConstants.ENVIRONMENT, environment, false, true);
  }
  return urlBuilder.getLocationObject();
}

export function getVenueUrl(
  venue: CsnSearchVenue,
  locale: Locale,
  cventGuestDomain: string,
  basePath: string,
  searchParams?: SearchParams,
  venueProfileFactParams?: VenueProfileFactParams
): string {
  const isBrandPage = venue.featuredLevel && venue.listingOwnerType === 'BRAND_PAGE';

  let venueUrl;
  if (isBrandPage && venue.brandName && venue.listingCode) {
    venueUrl = createBrandPageURLFromListing(
      venue.id,
      venue.brandName,
      cventGuestDomain,
      venue.listingCode,
      searchParams,
      venueProfileFactParams
    );
  } else if (venue.featuredLevel) {
    if (venue.adUrl) {
      venueUrl = createAdPageUrlFromPageUrl(venue.adUrl, cventGuestDomain, searchParams, venueProfileFactParams);
    } else {
      venueUrl = createAdPageURLFromListing(
        venue.id,
        venue.name,
        venue.type,
        venue.city,
        locale,
        cventGuestDomain,
        basePath,
        venue.listingCode,
        searchParams,
        venueProfileFactParams
      );
    }
  } else {
    venueUrl = createVenueURL(
      venue.id,
      venue.name,
      venue.type,
      venue.city,
      locale,
      cventGuestDomain,
      basePath,
      searchParams,
      venueProfileFactParams
    );
  }

  return new UrlBuilder().mergeLocationObject(venueUrl).getFullUrl();
}
